import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Lock, Building2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../App";
import { Btn, Pill, Logo } from "./ui";

const DEMO = {
  legal: { email: "admin@meridian.example", pass: "admin123" },
  accountancy: { email: "admin@nordhaven.example", pass: "admin123" },
  hr: { email: "admin@northwind.example", pass: "admin123" },
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [tenantId, setTenantId] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.tenants().then((t) => {
      const active = t.filter((x) => x.status === "active");
      setTenants(active);
      if (active[0]) setTenantId(active[0].id);
      setLoadingTenants(false);
    });
  }, []);

  const current = tenants.find((t) => t.id === tenantId);

  const fill = () => {
    const d = DEMO[current?.sector];
    if (d) {
      setEmail(d.email);
      setPassword(d.pass);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const data = await api.login({ tenant_id: tenantId, email, password });
      login(data);
      navigate("/");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lg-backdrop min-h-screen">
      <div className="lg-grain absolute inset-0 opacity-40" />
      <div className="relative mx-auto grid min-h-screen max-w-6xl grid-cols-1 items-center gap-12 px-6 py-10 lg:grid-cols-2">
        {/* Left brand pane */}
        <div className="hidden lg:block lg-rise">
          <Logo className="h-14" />
          <h1 className="mt-10 font-display text-4xl font-extrabold leading-tight tracking-tight xl:text-5xl">
            The AI Usage <span className="text-brand">Control Plane</span> for regulated European firms.
          </h1>
          <p className="mt-5 max-w-md text-slate-400">
            Sits between your people and any AI provider — detects sensitive data, applies
            layered policy, keeps sensitive content on EU-sovereign routes, and logs every
            request. Built to help prepare for AI Act readiness and support audit oversight.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            <Pill tone="brand">Detect</Pill>
            <Pill tone="gold">Layered Policy</Pill>
            <Pill tone="eu">EU Sovereign Routing</Pill>
            <Pill tone="muted">Immutable Audit</Pill>
          </div>
          <p className="mt-10 font-mono text-[11px] uppercase tracking-widest text-slate-600">
            Prototype · all companies &amp; data are fictional / synthetic
          </p>
        </div>

        {/* Right auth card */}
        <div className="lg-rise">
          <div className="mx-auto w-full max-w-md rounded-2xl border border-subtle bg-surface/90 p-8 shadow-2xl backdrop-blur-xl">
            <div className="mb-6 flex items-center lg:hidden">
              <Logo className="h-10" />
            </div>
            <h2 className="font-display text-xl font-bold">Sign in to your workspace</h2>
            <p className="mt-1 text-sm text-slate-400">Select a tenant, then authenticate.</p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-slate-500">
                  Tenant
                </label>
                <div className="relative">
                  <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <select
                    data-testid="tenant-select"
                    value={tenantId}
                    onChange={(e) => setTenantId(e.target.value)}
                    disabled={loadingTenants}
                    className="w-full appearance-none rounded-lg border border-subtle bg-elevated py-2.5 pl-9 pr-3 text-sm text-white outline-none focus:border-brand disabled:opacity-60"
                  >
                    {loadingTenants && <option>Loading tenants…</option>}
                    {tenants.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} — {t.sector}
                      </option>
                    ))}
                  </select>
                </div>
                {current && (
                  <p className="mt-1.5 font-mono text-[11px] text-slate-500">{current.jurisdiction}</p>
                )}
              </div>

              <div>
                <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-slate-500">
                  Email
                </label>
                <input
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-subtle bg-elevated px-3 py-2.5 text-sm outline-none focus:border-brand"
                  placeholder="you@firm.example"
                  required
                />
              </div>

              <div>
                <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-slate-500">
                  Password
                </label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <input
                    data-testid="login-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-lg border border-subtle bg-elevated py-2.5 pl-9 pr-3 text-sm outline-none focus:border-brand"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>

              {err && (
                <div data-testid="login-error" className="rounded-lg border border-block/40 bg-block/10 px-3 py-2 text-sm text-block">
                  {err}
                </div>
              )}

              <Btn data-testid="login-submit" type="submit" disabled={busy || loadingTenants || !tenantId} className="w-full">
                {busy ? "Authenticating…" : "Enter Control Plane"} <ArrowRight className="h-4 w-4" />
              </Btn>
            </form>

            <button
              type="button"
              data-testid="fill-demo"
              onClick={fill}
              className="mt-4 w-full rounded-lg border border-dashed border-subtle py-2 font-mono text-[11px] uppercase tracking-widest text-slate-500 transition-colors hover:border-brand/50 hover:text-brand"
            >
              Fill admin demo credentials
            </button>
            <p className="mt-3 text-center font-mono text-[11px] text-slate-600">
              Prototype auth — simple by design. Employee demo: use any employee email with
              password <span className="text-slate-400">demo123</span>.
            </p>
            <a
              href="/platform/login"
              data-testid="platform-staff-link"
              className="mt-4 flex items-center justify-center gap-1.5 border-t border-subtle pt-4 font-mono text-[11px] uppercase tracking-widest text-slate-600 transition-colors hover:text-brand"
            >
              LucidGate internal staff sign-in →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
